import React from "react";
import { Button, Col, FormGroup, Input, Label, Row } from "reactstrap";
import { QUALIFICATION_TYPES, REPEAT_MODES } from "../../../constants/promotionConstants";

const StepQualification = ({ promotion, onChange, menuItems, categories = [] }) => {
  const qualification = promotion.qualification;
  const entities = qualification.entities || [];

  // Derive brand options from menu items (brand is a flat field on products)
  const brandOptions = Array.from(
    new Set(menuItems.map((item) => item.brand).filter(Boolean))
  );

  // Hook to auto-initialize entities if empty but type is a row-based qualification
  React.useEffect(() => {
    const isRowBased = ["buyProduct", "buyCategory", "buyBrand", "buyCombo"].includes(qualification.type);
    if (isRowBased && entities.length === 0) {
      if (qualification.type === "buyProduct" && qualification.productIds?.length) {
        const rootQty = Number(qualification.quantity) || 1;
        const initial = qualification.productIds.map(p => ({
          type: "PRODUCT",
          entityId: p.productId,
          variantId: p.variantId || "any",
          quantity: rootQty
        }));
        onChange("entities", initial);
      } else if (qualification.type === "buyCategory" && qualification.categoryIds?.length) {
        const rootQty = Number(qualification.quantity) || 1;
        const initial = qualification.categoryIds.map(catId => ({
          type: "CATEGORY",
          entityId: catId,
          variantId: "any",
          quantity: rootQty
        }));
        onChange("entities", initial);
      } else if (qualification.type === "buyCombo" && qualification.comboItems?.length) {
        const initial = qualification.comboItems.map(item => ({
          type: "PRODUCT",
          entityId: item.productId,
          variantId: item.variantId || "any",
          quantity: Number(item.quantity) || 1
        }));
        onChange("entities", initial);
      } else {
        const defaultType = qualification.type === "buyCategory" ? "CATEGORY" : qualification.type === "buyBrand" ? "BRAND" : "PRODUCT";
        onChange("entities", [{ type: defaultType, entityId: "", variantId: "any", quantity: "1" }]);
      }
    }
  }, [qualification.type]);

  const getCategoryVariants = (categoryId) => {
    if (!categoryId) return [];
    const catIdNorm = String(categoryId).toLowerCase().trim();
    const products = menuItems.filter(p => {
      const pCatId = String(p.categoryId || "").toLowerCase().trim();
      return pCatId === catIdNorm;
    });
    const seen = new Set();
    const result = [];
    products.forEach(p => {
      const variants = p.variantValueIds || [];
      variants.forEach(v => {
        const vNorm = String(v).toLowerCase().trim();
        if (vNorm && !seen.has(vNorm)) {
          seen.add(vNorm);
          result.push(v);
        }
      });
    });
    return result;
  };

  const handleEntityItemChange = (index, field, value) => {
    const updated = [...entities];
    updated[index] = { ...updated[index], [field]: value };
    
    // Reset dependant fields on type/entity change
    if (field === "type") {
      updated[index].entityId = "";
      updated[index].variantId = "any";
    }
    if (field === "entityId") {
      updated[index].variantId = "any";
    }
    onChange("entities", updated);
  };

  const addEntityItem = () => {
    const defaultType = qualification.type === "buyCategory" ? "CATEGORY" : qualification.type === "buyBrand" ? "BRAND" : "PRODUCT";
    onChange("entities", [...entities, { type: defaultType, entityId: "", variantId: "any", quantity: "1" }]);
  };

  const removeEntityItem = (index) => {
    const updated = [...entities];
    updated.splice(index, 1);
    onChange("entities", updated);
  };

  const renderRow = (itemConfig, index) => {
    const type = itemConfig.type || "PRODUCT";
    const entityId = itemConfig.entityId || "";
    const variantId = itemConfig.variantId || "any";
    const quantity = itemConfig.quantity || "1";

    let entityOptions = [];
    if (type === "PRODUCT") {
      entityOptions = menuItems.map(item => ({ value: item.productId, label: item.name }));
    } else if (type === "CATEGORY") {
      entityOptions = categories.map(cat => ({ value: cat.id || cat.categoryId, label: cat.category }));
    } else if (type === "BRAND") {
      entityOptions = brandOptions.map(brand => ({ value: brand, label: brand }));
    }

    let availableVariants = ["any"];
    if (type === "PRODUCT") {
      const selectedProduct = menuItems.find(item => item.productId === entityId);
      const productVariants = selectedProduct?.variantEnabled
        ? (selectedProduct.variantValueIds || [])
        : ["standard"];
      availableVariants.push(...productVariants);
    } else if (type === "CATEGORY") {
      const categoryVariants = getCategoryVariants(entityId);
      availableVariants.push(...categoryVariants);
    } else {
      availableVariants.push("standard");
    }

    return (
      <Row key={index} className="align-items-end mb-3 g-2">
        <Col md={3}>
          <FormGroup className="mb-0">
            <Label className="small text-muted mb-1 font-weight-bold">Entity Type</Label>
            <Input
              type="select"
              value={type}
              onChange={(e) => handleEntityItemChange(index, "type", e.target.value)}
            >
              <option value="PRODUCT">Product</option>
              <option value="CATEGORY">Category</option>
              <option value="BRAND">Brand</option>
            </Input>
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup className="mb-0">
            <Label className="small text-muted mb-1 font-weight-bold">Entity</Label>
            <Input
              type="select"
              value={entityId}
              onChange={(e) => handleEntityItemChange(index, "entityId", e.target.value)}
            >
              <option value="">Select option</option>
              {entityOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup className="mb-0">
            <Label className="small text-muted mb-1 font-weight-bold">Variant</Label>
            <Input
              type="select"
              value={variantId}
              onChange={(e) => handleEntityItemChange(index, "variantId", e.target.value)}
            >
              {availableVariants.map(v => (
                <option key={v} value={v}>
                  {v === "any" ? "Any Variant" : v === "standard" ? "Standard" : v}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col md={2}>
          <FormGroup className="mb-0">
            <Label className="small text-muted mb-1 font-weight-bold">Qty</Label>
            <Input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => handleEntityItemChange(index, "quantity", e.target.value)}
            />
          </FormGroup>
        </Col>
        <Col md={1}>
          <Button color="danger" outline size="sm" className="mb-0 w-100 btn-block" onClick={() => removeEntityItem(index)}>
            Remove
          </Button>
        </Col>
      </Row>
    );
  };

  return (
    <div>
      <h5 className="mb-4">Qualification Rules</h5>
      <Row className="mb-4 g-3">
        <Col md={6}>
          <FormGroup>
            <Label for="qualificationType">Qualification Type</Label>
            <Input
              type="select"
              id="qualificationType"
              value={qualification.type}
              onChange={(e) => onChange("type", e.target.value)}
            >
              {QUALIFICATION_TYPES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
      </Row>

      {qualification.type === "spendAmount" && (
        <Row className="mb-4 g-3">
          <Col md={6}>
            <FormGroup>
              <Label for="minimumOrder">Minimum Order</Label>
              <Input
                id="minimumOrder"
                type="number"
                min="0"
                value={qualification.minimumOrder}
                onChange={(e) => onChange("minimumOrder", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
      )}

      {["buyProduct", "buyCategory", "buyBrand", "buyCombo"].includes(qualification.type) && (
        <div className="mb-4 border rounded p-3 bg-light">
          <div className="mb-3 d-flex justify-content-between align-items-center">
            <Label className="fw-bold mb-0">Required Items & Variants</Label>
            <Button color="secondary" outline size="sm" onClick={addEntityItem}>
              <i className="mdi mdi-plus me-1" /> Add Required Item
            </Button>
          </div>
          {entities.length > 0 ? (
            entities.map(renderRow)
          ) : (
            <div className="text-muted text-center py-2">No required items added yet. Click add to configure.</div>
          )}
        </div>
      )}

      {qualification.type === "custom" && (
        <Row className="mb-4">
          <Col md={12}>
            <FormGroup>
              <Label for="customRule">Custom Qualification Rule</Label>
              <Input
                type="textarea"
                id="customRule"
                rows={3}
                value={qualification.customRule}
                onChange={(e) => onChange("customRule", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
      )}

      {["buyProduct", "buyCategory", "buyBrand", "buyCombo"].includes(qualification.type) && (
        <Row className="mb-3">
          <Col md={4}>
            <FormGroup>
              <Label for="repeatMode">Repeat Qualification</Label>
              <Input
                type="select"
                id="repeatMode"
                value={qualification.repeatMode}
                onChange={(e) => onChange("repeatMode", e.target.value)}
              >
                {REPEAT_MODES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          {qualification.repeatMode === "LIMITED" && (
            <Col md={4}>
              <FormGroup>
                <Label for="repeatLimit">Maximum Repeat Count</Label>
                <Input
                  id="repeatLimit"
                  type="number"
                  min="1"
                  value={qualification.repeatLimit}
                  onChange={(e) => onChange("repeatLimit", e.target.value)}
                />
              </FormGroup>
            </Col>
          )}
        </Row>
      )}
    </div>
  );
};

export default StepQualification;
